import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import { useAuthStore } from "@/stores/auth";

interface Invitation {
  id: string;
  email: string;
  organizationId: string | null;
  role: "SUPER_ADMIN" | "ORG_ADMIN" | "MEMBER";
  status: "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED";
  expiresAt: string;
  createdAt: string;
  acceptedAt: string | null;
  invitedBy: string;
}

interface Organization {
  id: string;
  name: string;
  emailDomain: string;
}

type Tab = "ALL" | "PENDING" | "ACCEPTED" | "HISTORY";

export default function InvitationsPage() {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user)!;
  const isSuper = me.role === "SUPER_ADMIN";

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/organizations");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Organization[];
    },
  });

  const list = useQuery({
    queryKey: ["invitations"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/invitations");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Invitation[];
    },
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"ORG_ADMIN" | "MEMBER">("MEMBER");
  const [orgId, setOrgId] = useState<string>(me.organizationId ?? "");
  const [allowAnyEmail, setAllowAnyEmail] = useState(false);
  const [tab, setTab] = useState<Tab>("PENDING");

  const filtered = useMemo(() => {
    const all = list.data ?? [];
    switch (tab) {
      case "ALL": return all;
      case "PENDING": return all.filter((i) => i.status === "PENDING");
      case "ACCEPTED": return all.filter((i) => i.status === "ACCEPTED");
      case "HISTORY": return all.filter((i) => i.status === "REVOKED" || i.status === "EXPIRED");
    }
  }, [list.data, tab]);

  const counts = useMemo(() => {
    const all = list.data ?? [];
    return {
      ALL: all.length,
      PENDING: all.filter((i) => i.status === "PENDING").length,
      ACCEPTED: all.filter((i) => i.status === "ACCEPTED").length,
      HISTORY: all.filter((i) => i.status === "REVOKED" || i.status === "EXPIRED").length,
    };
  }, [list.data]);

  const create = useMutation({
    mutationFn: async (payload: { email: string; role: "ORG_ADMIN" | "MEMBER"; organizationId?: string; allowAnyEmail?: boolean }) => {
      const { data, error, response } = await api.POST("/api/v1/invitations", {
        body: payload,
      });
      if (error || !response.ok) throw error || new Error("发起失败");
      return data as unknown as Invitation;
    },
    onSuccess: (inv) => {
      setEmail("");
      setAllowAnyEmail(false);
      qc.invalidateQueries({ queryKey: ["invitations"] });
      toast.success(`邀请已发送至 ${inv.email}`);
      setTab("PENDING");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE("/api/v1/invitations/{id}", {
        params: { path: { id } },
      });
      if (error || !response.ok) throw error || new Error("撤销失败");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      toast.success("邀请已撤销");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">邀请管理</h1>
        <p className="text-sm text-muted-foreground mt-1">
          默认邀请邮箱需匹配目标公司的 email_domain 白名单；勾选"任意邮箱"后可邀请非公司域名后缀的邮箱
        </p>
      </div>

      <section className="rounded-lg border border-border bg-background p-5">
        <h2 className="font-medium mb-3">发起邀请</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({
              email,
              role,
              organizationId: orgId || undefined,
              allowAnyEmail: allowAnyEmail || undefined,
            });
          }}
          className="grid grid-cols-1 sm:grid-cols-4 gap-3"
        >
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="邮箱"
            type="email"
            required
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as "ORG_ADMIN" | "MEMBER")}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="MEMBER">MEMBER</option>
            <option value="ORG_ADMIN">ORG_ADMIN</option>
          </select>
          {isSuper ? (
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">选择公司</option>
              {orgsQuery.data?.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          ) : (
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {orgsQuery.data?.find((o) => o.id === me.organizationId)?.name ?? "本公司"}
            </div>
          )}
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? "发送中..." : "发起邀请"}
          </button>
          <label className="sm:col-span-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={allowAnyEmail}
              onChange={(e) => setAllowAnyEmail(e.target.checked)}
              className="h-4 w-4 rounded border-border"
            />
            邀请任意邮箱（跳过公司域名白名单，可邀请非公司域名后缀的邮箱）
          </label>
        </form>
      </section>

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        {(["PENDING", "ACCEPTED", "HISTORY", "ALL"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              tab === t
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tabLabel(t)} <span className="text-xs text-muted-foreground">({counts[t]})</span>
          </button>
        ))}
      </div>

      {/* desktop table */}
      <section className="rounded-lg border border-border bg-background overflow-hidden hidden sm:block">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left px-4 py-2 font-medium">邮箱</th>
              <th className="text-left px-4 py-2 font-medium">角色</th>
              <th className="text-left px-4 py-2 font-medium">状态</th>
              <th className="text-left px-4 py-2 font-medium">过期</th>
              <th className="text-left px-4 py-2 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">加载中…</td></tr>
            )}
            {filtered.length === 0 && !list.isLoading && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">该状态下没有邀请</td></tr>
            )}
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-border">
                <td className="px-4 py-2">{i.email}</td>
                <td className="px-4 py-2">{i.role}</td>
                <td className="px-4 py-2"><StatusPill status={i.status} /></td>
                <td className="px-4 py-2 text-xs text-muted-foreground">
                  {new Date(i.expiresAt).toLocaleString()}
                </td>
                <td className="px-4 py-2">
                  {i.status === "PENDING" && (
                    <button
                      onClick={() => {
                        if (confirm(`撤销对 ${i.email} 的邀请？`)) revoke.mutate(i.id);
                      }}
                      className="text-xs text-destructive hover:underline"
                    >
                      撤销
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* mobile cards */}
      <section className="space-y-2 sm:hidden">
        {list.isLoading && (
          <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">加载中…</div>
        )}
        {filtered.length === 0 && !list.isLoading && (
          <div className="rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground text-center">该状态下没有邀请</div>
        )}
        {filtered.map((i) => (
          <div key={i.id} className="rounded-lg border border-border bg-background p-4 space-y-1">
            <div className="flex items-start justify-between gap-2">
              <span className="font-medium break-all">{i.email}</span>
              <StatusPill status={i.status} />
            </div>
            <div className="text-xs text-muted-foreground">角色：{i.role}</div>
            <div className="text-xs text-muted-foreground">
              过期：{new Date(i.expiresAt).toLocaleString()}
            </div>
            {i.status === "PENDING" && (
              <button
                onClick={() => {
                  if (confirm(`撤销对 ${i.email} 的邀请？`)) revoke.mutate(i.id);
                }}
                className="text-xs text-destructive hover:underline pt-1"
              >
                撤销
              </button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

function tabLabel(t: Tab): string {
  switch (t) {
    case "ALL": return "全部";
    case "PENDING": return "待接受";
    case "ACCEPTED": return "已接受";
    case "HISTORY": return "历史";
  }
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === "PENDING"
      ? "bg-amber-100 text-amber-800"
      : status === "ACCEPTED"
      ? "bg-emerald-100 text-emerald-800"
      : status === "REVOKED"
      ? "bg-gray-200 text-gray-700"
      : "bg-rose-100 text-rose-800";
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{status}</span>;
}
